import { Typography } from "@/components/atoms";
import { BedIcon, ExpandIcon, ShowerHeadIcon } from "@/components/atoms/icons";
import { useTranslation, useUIColors } from "@/core/hooks";
import { useI18nContext } from "@/core/providers/I18nProvider";
import React from "react";
import { Platform, StyleSheet, View } from "react-native";

export interface PropertyStatItemProps {
  icon: React.ReactNode;
  number: string | number;
  label: string;
  layout?: "grid" | "list";
}

export function PropertyStatItem({
  icon,
  number,
  label,
  layout = "grid",
}: Readonly<PropertyStatItemProps>) {
  const { isRTL } = useI18nContext();

  if (layout === "list") {
    return (
      <View style={styles.stacked}>
        <View
          style={[
            styles.topRow,
            isRTL && styles.topRowRTL,
            isRTL && Platform.OS !== "web" && styles.topRowRTLNative,
          ]}
        >
          {icon}
          <Typography variant="headline" color="primary" style={styles.number}>
            {number}
          </Typography>
        </View>
        <Typography
          variant="footnote"
          color="tertiary"
          numberOfLines={1}
          style={[
            styles.label,
            isRTL && Platform.OS !== "web" ? styles.labelStart : styles.labelFullWidth,
            isRTL && styles.labelRTL,
          ]}
        >
          {label}
        </Typography>
      </View>
    );
  }

  return (
    <View style={styles.inline}>
      {icon}
      <Typography variant="footnote" color="tertiary">
        <Typography variant="headline" color="primary" style={styles.number}>
          {number}
        </Typography>{" "}
        {label}
      </Typography>
    </View>
  );
}

export interface PropertyStatsRowProps {
  /** Omit/0 hides the Bedrooms stat instead of showing a placeholder. */
  bedrooms?: number | null;
  /** Omit/0 hides the Bathrooms stat instead of showing a placeholder. */
  bathrooms?: number | null;
  /** Omit/0 hides the Area stat instead of showing a placeholder. */
  areaSqFt?: number | null;
  /** Preformatted area display used by the ADHA listing flow. */
  areaSqFtDisplay?: string | undefined;
  layout?: "grid" | "list";
}

export function PropertyStatsRow({
  bedrooms,
  bathrooms,
  areaSqFt,
  areaSqFtDisplay,
  layout = "grid",
}: Readonly<PropertyStatsRowProps>) {
  const c = useUIColors();
  const { t } = useTranslation();
  const { isRTL } = useI18nContext();

  if (!bedrooms && !bathrooms && !areaSqFt) {
    return null;
  }

  // Mobile: plain row — native already auto-mirrors this under RTL (see
  // PropertyStatItem's icon/number pairing below, which mirrors correctly
  // with zero RTL-conditional code); an isRTL-conditional reverse here would
  // double-flip it back to the wrong side.
  // Web: unchanged from before this pass — keeps its original manual reverse.
  return (
    <View
      style={[
        styles.row,
        Platform.OS === "web" && isRTL && { flexDirection: "row-reverse" },
      ]}
    >
      {Boolean(bedrooms) && (
        <PropertyStatItem
          icon={<BedIcon size={16} color={c.fontTertiary} />}
          number={bedrooms!}
          label={t("propertyDetails.bedrooms")}
          layout={layout}
        />
      )}
      {Boolean(bathrooms) && (
        <PropertyStatItem
          icon={<ShowerHeadIcon size={16} color={c.fontTertiary} />}
          number={bathrooms!}
          label={t("propertyDetails.bathrooms")}
          layout={layout}
        />
      )}
      {Boolean(areaSqFt) && (
        <PropertyStatItem
          icon={<ExpandIcon size={16} color={c.fontTertiary} />}
          number={
            areaSqFtDisplay ??
            areaSqFt!.toLocaleString(undefined, { maximumFractionDigits: 1 })
          }
          label={t("properties.sqFt")}
          layout={layout}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: "row",
    flexWrap: "nowrap",
    gap: 4,
    alignItems: "flex-start",
    width: "100%",
  },
  inline: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingVertical: 12,
    flexShrink: 1,
  },
  stacked: {
    flexGrow: 1,
    flexShrink: 0,
    paddingBottom: 25,
  },
  topRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  // Icon/number should stay in authored order (icon first) in both
  // languages — direction:'ltr' stops native RTL auto-mirror from swapping
  // them.
  topRowRTL: {
    direction: "ltr",
  },
  // Mobile Arabic: the ltr lock above also packs the icon/number from the
  // left edge; flex-end (= right under ltr) lines it up with the
  // right-aligned label below, keeping icon-first order.
  topRowRTLNative: {
    justifyContent: "flex-end",
  },
  number: {
    textAlign: "center",
    letterSpacing: 0,
  },
  label: {
    position: "absolute",
    bottom: 0,
  },
  labelFullWidth: {
    left: 0,
    right: 0,
  },
  // Mobile Arabic: pin the label's box to the item's right edge instead of
  // relying on textAlign inside a full-width box — native can swap
  // textAlign left/right under RTL, which sent 'right' back to the left.
  // `start` because the stacked item itself is RTL (start = right there).
  labelStart: {
    start: 0,
  },
  // Typography hardcodes textAlign:'left' on native (relies on native RTL
  // auto-mirror for normal flowing text) — this label is absolutely
  // positioned across the full width instead, so it needs an explicit
  // override to actually flip sides in Arabic.
  labelRTL: {
    textAlign: "right",
  },
});

export default PropertyStatsRow;
